import React, { useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import lottie from "lottie-web";

export default function Lottie(props) {
  const {
    width,
    height,
    ariaRole,
    ariaLabel,
    isClickToPauseDisabled,
    title,
    options,
    eventListeners,
    isStopped,
    segments,
    testId,
    mockLottieObjOverrides
  } = props;
  const { loop, autoplay, animationData, rendererSettings } = options;
  let animRef = useRef(null);
  let el = useRef();

  const deRegisterEvents = useCallback(
    eventListeners => {
      const anim = animRef.current;
      eventListeners.forEach(eventListener => {
        anim.removeEventListener(
          eventListener.eventName,
          eventListener.callback
        );
      });
    },
    [animRef]
  );

  const setSpeed = useCallback(() => {
    const anim = animRef.current;
    anim.setSpeed(props.speed);
  }, [props.speed, animRef]);

  const setDirection = useCallback(() => {
    const anim = animRef.current;
    anim.setDirection(props.direction);
  }, [props.direction, animRef]);

  const registerEvents = useCallback(
    eventListeners => {
      const anim = animRef.current;
      eventListeners.forEach(eventListener => {
        anim.addEventListener(eventListener.eventName, eventListener.callback);
      });
    },
    [animRef]
  );

  useEffect(() => {
    const loadOptions = {
      ...{
        container: el.current,
        renderer: "svg",
        loop: loop !== false,
        autoplay: autoplay !== false,
        segments: segments !== false,
        animationData,
        rendererSettings
      },
      ...options
    };

    animRef.current = lottie.loadAnimation(loadOptions);
    if (mockLottieObjOverrides) {
      Object.keys(mockLottieObjOverrides).forEach(key => {
        animRef.current[key] = mockLottieObjOverrides[key];
      });
    }
    setSpeed();
    setDirection();
    registerEvents(eventListeners);
    // Remove event listener on cleanup
    return () => {
      const anim = animRef.current;
      deRegisterEvents(eventListeners);
      anim.destroy();
    };
  }, [
    mockLottieObjOverrides,
    animationData,
    autoplay,
    eventListeners,
    loop,
    options,
    rendererSettings,
    segments,
    deRegisterEvents,
    registerEvents,
    setSpeed,
    setDirection
  ]);

  useEffect(() => {
    const anim = animRef.current;
    if (isStopped) {
      anim.stop();
    } else if (segments) {
      anim.playSegments(segments);
    } else {
      anim.play();
    }
  }, [isStopped, segments, animRef]);

  const handleClickToPause = useCallback(() => {
    const anim = animRef.current;
    if (anim.isPaused) {
      anim.play();
    } else {
      if (!props.isPaused && !anim.isPaused) {
        anim.pause();
      }
    }
  }, [props.isPaused]);

  const getSize = initial => {
    let size;
    if (typeof initial === "number") {
      size = `${initial}px`;
    } else {
      size = initial || "100%";
    }
    return size;
  };
  const lottieStyles = {
    width: getSize(width),
    height: getSize(height),
    overflow: "hidden",
    margin: "0 auto",
    outline: "none",
    ...props.style
  };
  const onClickHandler = isClickToPauseDisabled
    ? () => null
    : handleClickToPause;
  return (
    <div
      ref={el}
      style={lottieStyles}
      onClick={onClickHandler}
      title={title}
      role={ariaRole}
      aria-label={ariaLabel}
      tabIndex="0"
      data-test-id={testId}
    />
  );
}

Lottie.propTypes = {
  /** This is an array of objects containing a eventName and callback function that will be registered as eventlisteners on the animation object.
   * refer to bodymovin#events where the mention using addEventListener, for a list of available custom events. */
  eventListeners: PropTypes.arrayOf(
    PropTypes.shape({
      eventName: PropTypes.string,
      callback: PropTypes.func
    })
  ),
  /** The object representing the animation settings that will be instantiated by bodymovin.
   * Currently a subset of the bodymovin options are supported:
   * loop options (default false)
   * autoplay options default false
   * animationData required
   * rendererSettings required*/
  options: PropTypes.object.isRequired,
  /** pixel value for containers height default 100% */
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** pixel value for containers width default 100% */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** set to true to stop the animation */
  isStopped: PropTypes.bool,
  /** set to true to pause the animation  */
  isPaused: PropTypes.bool,
  /** Animation of speed 1 is normal speed */
  speed: PropTypes.number,
  /** First param is a single array  of two values each(fromFrame,toFrame) */
  segments: PropTypes.arrayOf(PropTypes.number),
  /** One param direction (1 is normal direction.) */
  direction: PropTypes.number,
  /** Defining a type of user interface element i.e.: button, checkbox, link, option, radio slider, tab */
  ariaRole: PropTypes.string,
  /** A string that describes the animation */
  ariaLabel: PropTypes.string,
  /** Allow the user to stop/start the animation when clicking on it */
  isClickToPauseDisabled: PropTypes.bool,
  /**  Extra information about an element shown as a tooltip text when the mouse moves over the element */
  title: PropTypes.string,
  /** Style Overrides for animation div */
  style: PropTypes.string,
  /** Id for automation testing */
  testId: PropTypes.string
};

Lottie.defaultProps = {
  eventListeners: [],
  isStopped: false,
  isPaused: false,
  speed: 1,
  ariaRole: "button",
  ariaLabel: "animation",
  isClickToPauseDisabled: false,
  title: ""
};
